import { useApi } from '@/api';
import { ChartSkeleton, ChartTimeFilter } from '@/components';
import { useChartPeriod } from '@/hooks';
import { tokensMapping } from '@/utils/tokens';
import {
  CurrencyValue,
  DisplaySwitch,
  Flex,
  LineChart,
  formatNumber
} from '@blockanalitica/ui';
import { useMemo, useState } from 'react';

export default function HistoricTabs() {
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const timeFilter = useMemo(
    () => (
      <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
    ),
    [chartDaysAgo]
  );
  const [activeDisplayOption, setActiveDisplayOption] = useState('sky_balance');
  const [, updateChartConfigWithPeriod] = useChartPeriod(chartDaysAgo);
  const config = updateChartConfigWithPeriod();

  const displayOptions = useMemo(
    () => [
      { key: 'sky_balance', value: 'SKY' },
      { key: 'mkr_balance', value: 'MKR' }
    ],
    []
  );
  const [data, error] = useApi({
    url: '/migrations/mkr-sky/historic/',
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  if (error) {
    throw error;
  }

  const valueFormatter = useMemo(() => {
    function formatValue(value: string) {
      switch (activeDisplayOption) {
        case 'sky_balance':
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: tokensMapping('SKY'),
                iconSize: '3'
              }}
            />
          );
        default:
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: tokensMapping('MKR'),
                iconSize: '3'
              }}
            />
          );
      }
    }
    return formatValue;
  }, [activeDisplayOption]);

  const yScaleValueFormatter = (value) => {
    return formatNumber(Number(value));
  };

  const datasets = useMemo(() => {
    return [
      {
        xField: 'date',
        yField: activeDisplayOption
      }
    ];
  }, [activeDisplayOption]);

  if (data) {
    return (
      <Flex flexDirection="column" gap="xs">
        <DisplaySwitch
          data={displayOptions}
          activeOption={activeDisplayOption}
          onChange={setActiveDisplayOption}
        />

        <LineChart
          key={activeDisplayOption}
          data={data}
          valueFormatter={valueFormatter}
          datasets={datasets}
          yScaleValueFormatter={yScaleValueFormatter}
          timeFilter={timeFilter}
          config={config}
        />
      </Flex>
    );
  }

  return <ChartSkeleton />;
}
